import {Component, computed, inject, signal} from '@angular/core';
import {Card} from 'primeng/card';
import {TableModule} from 'primeng/table';
import {AdminService, BasicTaDetails, ComplianceDashboardCounts, FullTaDetails, TAStatus} from '../../../api';
import {TaInfoCardComponent} from '../../components/ta-info-card/ta-info-card.component';
import {TaListBase} from '../ta-list-base.directive';
import {ComplianceDashboardTab, TaCardAction, toTAStatusText} from '../../../util/app.types';
import {Button} from 'primeng/button';
import {Chip} from 'primeng/chip';
import {DatePipe, NgClass} from '@angular/common';
import {FormsModule} from '@angular/forms';
import {InputText} from 'primeng/inputtext';
import {TabsModule} from 'primeng/tabs';
import {AllSubmissionsSearchComponent} from '../../components/ta-list-tables/all-submissions-search/all-submissions-search.component';
import {PrepaidIndicatorComponent} from '../../components/prepaid-indicator/prepaid-indicator.component';
import {AttachmentIndicatorComponent} from '../../components/attachment-indicator/attachment-indicator.component';
import {TaCostBubbleComponent} from '../../components/ta-cost-bubble/ta-cost-bubble.component';
import {taOverpaidAmount, taPaymentComponents} from '../../../util/general.utils';
import {TaCommentCount} from '../../components/ta-comments/ta-comment-count';
import {Checkbox} from 'primeng/checkbox';
import {ConfirmationWrapperService} from '../../../services/confirmation-wrapper.service';
import {ErrorMessageService} from '../../../services/error-message.service';
import {HttpResponse} from '@angular/common/http';
import {Badge} from 'primeng/badge';
import {ScrollHeightDirective} from '../../directives/scroll-height.directive';

@Component({
  selector: 'clc-compliance-dashboard-page',
  imports: [
    Card,
    TableModule,
    TaInfoCardComponent,
    Button,
    Chip,
    DatePipe,
    FormsModule,
    InputText,
    NgClass,
    TabsModule,
    AllSubmissionsSearchComponent,
    PrepaidIndicatorComponent,
    AttachmentIndicatorComponent,
    TaCostBubbleComponent,
    TaCommentCount,
    Checkbox,
    Badge,
    ScrollHeightDirective,
  ],
  templateUrl: './clc-compliance-dashboard-page.html',
  styleUrl: './clc-compliance-dashboard-page.scss'
})
export class ClcComplianceDashboardPage extends TaListBase {
  adminApi = inject(AdminService);
  confirmationServiceWrapper = inject(ConfirmationWrapperService);
  private errorMessageService = inject(ErrorMessageService);

  tabOption: string = 'overpaid';
  canProcessDayTripPayment = computed(() => this.permissionService.canProcessDayTripPayment());
  // Selection column hidden until the day trip batch endpoint exists (see processDayTripPaymentsBatch).
  readonly dayTripBatchEnabled = false;
  exporting = signal(false);
  protected readonly taOverpaidAmount = taOverpaidAmount;
  protected readonly taPaymentComponents = taPaymentComponents;
  // Per tab TA counts; every tab load returns the counts for all tabs.
  counts = signal<ComplianceDashboardCounts>({});

  exportComplianceTas(): void {
    const xlsxType = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
    this.exporting.set(true);
    this.adminApi.getComplianceExport('response', false, {httpHeaderAccept: xlsxType as any}).subscribe({
      next: (response: HttpResponse<any>) => {
        const url = window.URL.createObjectURL(new Blob([response.body], {type: xlsxType}));
        const a = document.createElement('a');
        a.href = url;
        const disposition = response.headers.get('Content-Disposition');
        const filenameMatch = disposition?.match(/filename="([^"]+)"|filename=([^;\s]+)/);
        a.download = filenameMatch?.[1] ?? filenameMatch?.[2] ?? 'Compliance_TAs_Export.xlsx';
        a.click();
        window.URL.revokeObjectURL(url);
        this.exporting.set(false);
      },
      error: err => {
        console.error('Compliance TAs export failed', err);
        this.errorMessageService.showTemporaryErrorWithTitle('Export Failed', 'The Compliance TAs export could not be generated.', 8000);
        this.exporting.set(false);
      },
    });
  }

  resetSelectedTa(): void {
    this.store.resetTaState();
  }

  // Both list tabs share availableTaList (and the base filter/selection), so a tab change reloads it.
  tabChanged(): void {
    this.resetSelectedTa();
    this.searchStr.set('');
    this.availableTaList.set([]);
    this.loadTaList();
  }

  override ngOnInit(): void {
    super.ngOnInit();

    this.store.updateBreadCrumbData({
      title: 'Compliance Dashboard',
      titleIcon: 'fa-regular fa-shield-check fa-lg'
    });
    this.loadTaList();
  }

  override viewTaSubmission(taSubmission: FullTaDetails | BasicTaDetails) {
    this.store.updatePreviousPageRoute(this.router.url);
    this.router.navigate([`/ta/${taSubmission.id}/manage`]);
  }

  loadTaList(): void {
    if (this.tabOption === 'dayTrips') {
      this.loadDayTripList();
    } else if (this.tabOption === 'overpaid') {
      this.loadOverpaidList();
    }
  }

  loadDayTripList(): void {
    this.loadComplianceTab('dayTrips', 'Loading Day Trip Payments');
  }

  loadOverpaidList(): void {
    this.loadComplianceTab('overpaid', 'Loading Managed TAs');
  }

  private loadComplianceTab(tab: ComplianceDashboardTab, loadingMessage: string): void {
    this.loadingService.showLoading({message: loadingMessage, targetElement: this.loadingRef});

    this.taSubmissionDatasource.loadComplianceDashboardTab(tab).subscribe({
      next: resp => {
        if (!!resp.data) {
          this.availableTaList.set(resp.data.results ?? []);
          if (!!resp.data.counts) {
            this.counts.set(resp.data.counts);
          }
        } else {
          console.error(`Null ${tab} submissions data`, resp);
        }
        this.loadingService.hideLoading();
      },
      error: err => {
        console.log(err);
        this.loadingService.hideLoading();
      }
    });
  }

  processDayTripPaymentClicked(taSubmission?: FullTaDetails | BasicTaDetails): void {
    if (!taSubmission) {
      return;
    }
    this.confirmationServiceWrapper.processDayTripPaymentConfirm(taSubmission).subscribe({
      next: () => {
        this.resetSelectedTa();
        this.loadDayTripList();
      },
      error: err => {
        console.error('Error processing day trip payment: ', err);
      },
    });
  }

  // The server-side batch is a placeholder (see processDayTripPaymentsBatch); success and partial
  // success both just reload the tab.
  processSelectedDayTripPaymentsClicked(): void {
    const taSubmissions = this.selectedTaList();
    if (taSubmissions.length === 0) {
      return;
    }
    this.confirmationServiceWrapper.processDayTripPaymentBatchConfirm(taSubmissions).subscribe({
      next: () => {
        this.resetSelectedTa();
        this.loadDayTripList();
      },
      error: err => {
        console.error('Error processing selected day trip payments: ', err);
      },
    });
  }

  taActionClicked(action: TaCardAction): void {
    if (action === 'dayTripPayment') {
      this.processDayTripPaymentClicked(this.selectedTa());
    }
  }

  // Every row on the Day Trip Payments tab has a pending payment; Finalised (multi-day) rows keep their status.
  dayTripStatusText(status: TAStatus): string {
    return status === TAStatus.AcquittalApproved ? 'Ready for Day Trip Payment' : toTAStatusText(status);
  }

  protected readonly toTAStatusText = toTAStatusText;
}
